import React, {useMemo, useState} from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Easing,
  Sequence,
} from "remotion";
import { Blur } from "../Background/Blur";
import { z } from "zod";
import CroppedVideo from "../components/CroppedVideo";
import CroppedImg from "../components/CroppedImg";
import { resolveMediaSrc } from "../utils/mediaSrc";

/* ── Media helpers (same pattern as UnboxingOverview) ────────── */
const getMediaSrc = (src) => resolveMediaSrc(src);

const isVideoSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  const clean = src.split(/[?#]/)[0];
  return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(clean);
};

const resolveMediaType = (item) => {
  if (item?.type === "video" || item?.type === "image") return item.type;
  return isVideoSrc(item?.media || item?.src) ? "video" : "image";
};

/* ── Zod schema ─────────────────────────────────────────────── */
const TopCountdownPropsSchema = z.object({
  title: z.string().default("TOP 5"),
  subtitle: z.string().default("(BEST TILL LAST)"),
  items: z.array(z.object({
    label: z.string(),
    media: z.string().optional().describe("URL or staticFile path for a video or image"),
    type: z.enum(["video", "image"]).optional().describe("Explicit media type; auto-detected from extension if omitted"),
    crop: z.object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
      sourceWidth: z.number().optional(),
      sourceHeight: z.number().optional(),
    }).nullable().optional().describe("Optional crop rect"),
    videoStartFrom: z.number().default(0).describe("For video: start playback from this frame"),
    videoEndAt: z.number().optional().describe("For video: stop playback at this frame"),
  })).default([]),
  startFrame: z.number().min(0).default(0),
  itemInFrames: z.number().positive().default(14),
  staggerFrames: z.number().positive().default(45),
  revealAtFrames: z.array(z.number()).optional(),
  theme: z.object({
    bg: z.string().default("#000000"),
    primary: z.string().default("#ff2c55"),
    accent: z.string().optional().default("#ff6b35"),
    text: z.string().default("#ffffff"),
  }).default({
    bg: "#000000",
    primary: "#ff2c55",
    accent: "#ff6b35",
    text: "#ffffff",
  }),
});

export { TopCountdownPropsSchema };

/* ── Reusable media renderers using CroppedVideo / CroppedImg ─ */
const MediaRenderer = ({item, style = {}}) => {
  const [videoError, setVideoError] = useState(false);
  const src = getMediaSrc(item?.media);
  if (!src) return null;

  const mediaType = resolveMediaType(item);
  const crop = item?.crop ?? null;

  if (mediaType === "video") {
    if (videoError) {
      return (
        <div style={{width: "100%", height: "100%", background: "#111", color: "#ddd", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Arial, sans-serif"}}>
          Media failed to load
        </div>
      );
    }
    return (
      <CroppedVideo
        src={src}
        crop={crop}
        startFrom={item.videoStartFrom || 0}
        endAt={item.videoEndAt}
        muted
        loop
        onError={() => setVideoError(true)}
        style={{width: "100%", height: "100%", objectFit: "cover", ...style}}
        containerStyle={{width: "100%", height: "100%"}}
      />
    );
  }

  return (
    <CroppedImg
      src={src}
      crop={crop}
      style={{width: "100%", height: "100%", objectFit: "cover", ...style}}
      containerStyle={{width: "100%", height: "100%"}}
    />
  );
};

const BlurBackground = ({item}) => (
  <MediaRenderer item={item} />
);

/* ── Deterministic pseudo-random for particles ──────────────── */
const seededRandom = (seed) => {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
};

/* ── Animated particle burst ────────────────────────────────── */
const ParticleBurst = ({frame, startAt, color, cx, cy, count = 12}) => {
  const elapsed = frame - startAt;
  if (elapsed < 0 || elapsed > 40) return null;
  const progress = elapsed / 40;
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const angle = (i / count) * Math.PI * 2 + seededRandom(i + startAt) * 0.5;
        const dist = 60 + seededRandom(i * 7 + startAt) * 140;
        const size = 3 + seededRandom(i * 3) * 5;
        const x = cx + Math.cos(angle) * dist * progress;
        const y = cy + Math.sin(angle) * dist * progress;
        const opacity = interpolate(progress, [0, 0.3, 1], [0, 1, 0], {extrapolateRight: "clamp"});
        return (
          <div key={i} style={{
            position: "absolute",
            left: x - size / 2,
            top: y - size / 2,
            width: size,
            height: size,
            borderRadius: "50%",
            background: color,
            opacity,
            boxShadow: `0 0 ${size * 2}px ${color}`,
            pointerEvents: "none",
          }} />
        );
      })}
    </>
  );
};

/* ── Expanding ring effect ──────────────────────────────────── */
const EnergyRing = ({frame, startAt, color, cx, cy, delay = 0}) => {
  const elapsed = frame - startAt - delay;
  if (elapsed < 0 || elapsed > 30) return null;
  const progress = elapsed / 30;
  const radius = 20 + progress * 180;
  const opacity = interpolate(progress, [0, 0.2, 1], [0, 0.7, 0]);
  return (
    <div style={{
      position: "absolute",
      left: cx - radius,
      top: cy - radius,
      width: radius * 2,
      height: radius * 2,
      borderRadius: "50%",
      border: `2px solid ${color}`,
      opacity,
      boxShadow: `0 0 20px ${color}66, inset 0 0 20px ${color}22`,
      pointerEvents: "none",
    }} />
  );
};

/* ── Floating ambient particles ─────────────────────────────── */
const AmbientParticles = ({frame, width, height, color, count = 20}) => (
  <>
    {Array.from({length: count}, (_, i) => {
      const speed = 0.3 + seededRandom(i * 13) * 0.7;
      const x = seededRandom(i * 7) * width;
      const baseY = height + 20 - ((frame * speed + seededRandom(i * 31) * height * 2) % (height + 40));
      const drift = Math.sin(frame * 0.03 + i * 2) * 30;
      const size = 2 + seededRandom(i * 5) * 3;
      const opacity = 0.15 + seededRandom(i * 11) * 0.25;
      return (
        <div key={i} style={{
          position: "absolute",
          left: x + drift,
          top: baseY,
          width: size,
          height: size,
          borderRadius: "50%",
          background: color,
          opacity,
          boxShadow: `0 0 ${size * 3}px ${color}`,
          pointerEvents: "none",
        }} />
      );
    })}
  </>
);

/* ── Animated diagonal stripe overlay ───────────────────────── */
const DiagonalStripes = ({frame, color, opacity = 0.03}) => {
  const offset = (frame * 2) % 80;
  return (
    <div style={{
      position: "absolute",
      inset: -100,
      opacity,
      backgroundImage: `repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 35px,
        ${color} 35px,
        ${color} 40px
      )`,
      backgroundPosition: `${offset}px 0`,
      pointerEvents: "none",
    }} />
  );
};

/* ── Crown SVG for #1 ───────────────────────────────────────── */
const CrownIcon = ({size = 48, color = "#ffd700"}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M2 17L4 8L8 12L12 4L16 12L20 8L22 17H2Z"
      fill={color}
      stroke={color}
      strokeWidth="0.5"
    />
    <rect x="2" y="17" width="20" height="3" rx="1" fill={color} />
    <circle cx="5" cy="19" r="1" fill="#fff" opacity="0.4" />
    <circle cx="12" cy="19" r="1" fill="#fff" opacity="0.4" />
    <circle cx="19" cy="19" r="1" fill="#fff" opacity="0.4" />
  </svg>
);

/* ── Progress bar ───────────────────────────────────────────── */
const ProgressBar = ({activeIndex, total, color, accent, width: barW}) => {
  const segW = (barW - (total - 1) * 6) / total;
  return (
    <div style={{display: "flex", gap: 6, alignItems: "center"}}>
      {Array.from({length: total}, (_, i) => {
        const filled = i <= activeIndex;
        return (
          <div key={i} style={{
            width: segW,
            height: 5,
            borderRadius: 3,
            background: filled
              ? `linear-gradient(90deg, ${color}, ${accent || color})`
              : "rgba(255,255,255,0.12)",
            boxShadow: filled ? `0 0 10px ${color}66` : "none",
          }} />
        );
      })}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   TopCountdown – YouTube-optimized cinematic reveal
   ════════════════════════════════════════════════════════════════ */
export const TopCountdown = (rawProps) => {
  const {
    title,
    subtitle,
    items,
    startFrame,
    itemInFrames,
    staggerFrames,
    revealAtFrames,
    theme,
  } = TopCountdownPropsSchema.parse(rawProps);

  const frame = useCurrentFrame();
  const {width, height, fps} = useVideoConfig();
  const n = items.length;

  const rel = useMemo(() => {
    if (Array.isArray(revealAtFrames) && revealAtFrames.length === n) {
      return revealAtFrames;
    }
    return Array.from({length: n}, (_, i) => i * staggerFrames);
  }, [revealAtFrames, n, staggerFrames]);

  const starts = rel.map((r) => startFrame + r);
  const ends = [...starts.slice(1), startFrame + rel[rel.length - 1] + 99999];
  const activeIndex = starts.reduce((acc, s, i) => (frame >= s ? i : acc), -1);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
  const currentMedia = activeItem?.media;

  // Pulse for glow effects
  const pulse = Math.sin(frame * 0.08) * 0.2 + 0.8;
  const fastPulse = Math.sin(frame * 0.15) * 0.3 + 0.7;

  // Intro animations
  const introSpring = spring({frame, fps, config: {damping: 14, stiffness: 80, mass: 0.6}});
  const titleScale = interpolate(introSpring, [0, 1], [1.8, 1]);
  const titleOpacity = interpolate(introSpring, [0, 1], [0, 1]);

  // Media card layout
  const cardW = Math.min(Math.round(width * 0.82), 880);
  const cardH = Math.round(cardW * 10 / 9);
  const cardLeft = Math.round((width - cardW) / 2);
  const cardTop = Math.round(height * 0.42);

  // List layout
  const listItemH = Math.round(height * 0.058);
  const listTop = Math.round(height * 0.165);

  return (
    <Blur
      background={
        currentMedia ? (
          <BlurBackground item={activeItem} />
        ) : (
          <div style={{background: theme.bg, width: "100%", height: "100%"}} />
        )
      }
      zoom={1.35}
      darken={0.65}
      vignette={0.5}
      blurMin={25}
      blurMax={40}
      pulseHz={0.06}
    >
      <AbsoluteFill style={{overflow: "hidden"}}>

        {/* ── Animated background textures ──────────────────────── */}
        <DiagonalStripes frame={frame} color={theme.primary} opacity={0.025} />
        <AmbientParticles frame={frame} width={width} height={height} color={theme.primary} count={18} />

        {/* ── Dark gradient overlays for depth ─────────────────── */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `
            radial-gradient(ellipse 80% 50% at 50% 110%, ${theme.primary}22 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 80% 0%, ${theme.accent || theme.primary}11 0%, transparent 50%),
            linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 25%, transparent 70%, rgba(0,0,0,0.6) 100%)
          `,
        }} />

        {/* ── Header: Cinematic slam-in ────────────────────────── */}
        <div style={{
          position: "absolute",
          left: 0, right: 0, top: 28,
          textAlign: "center",
          transform: `scale(${titleScale})`,
          opacity: titleOpacity,
        }}>
          {/* Title */}
          <div style={{
            fontFamily: "'Impact', 'Anton', 'Oswald', system-ui, sans-serif",
            fontSize: 82,
            fontWeight: 900,
            letterSpacing: 10,
            lineHeight: 1,
            color: theme.text,
            textTransform: "uppercase",
            textShadow: `
              0 0 40px ${theme.primary}88,
              0 0 80px ${theme.primary}44,
              0 4px 0 ${theme.primary},
              0 6px 20px rgba(0,0,0,0.5)
            `,
            WebkitTextStroke: `1px ${theme.primary}66`,
          }}>
            {title}
          </div>

          {subtitle && (
            <div style={{
              marginTop: 6,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: 8,
              color: theme.accent || theme.primary,
              textTransform: "uppercase",
              textShadow: `0 0 20px ${theme.accent || theme.primary}66`,
              opacity: 0.9,
            }}>
              {subtitle}
            </div>
          )}

          {/* Progress bar under title */}
          <div style={{
            display: "flex", justifyContent: "center", marginTop: 16,
            opacity: interpolate(introSpring, [0, 1], [0, 1]),
          }}>
            <ProgressBar
              activeIndex={activeIndex}
              total={n}
              color={theme.primary}
              accent={theme.accent}
              width={Math.round(width * 0.55)}
            />
          </div>
        </div>

        {/* ── Countdown list (side strip) ──────────────────────── */}
        <div style={{
          position: "absolute",
          left: 28,
          right: 28,
          top: listTop,          zIndex: 10,        }}>
          {items.map((it, i) => {
            const rank = n - i;
            const isActive = i === activeIndex;
            const isPast = activeIndex > i;
            const isNumber1 = rank === 1;
            const elapsed = frame - starts[i];

            // Slam-in entrance
            const slamSpring = spring({
              frame: elapsed,
              fps,
              config: {damping: 10, stiffness: 200, mass: 0.7},
            });
            const slideX = interpolate(slamSpring, [0, 1], [width * 0.5, 0]);
            const itemOpacity = interpolate(slamSpring, [0, 1], [0, 1]);
            const slamScale = interpolate(slamSpring, [0, 1], [1.4, 1]);
            const rotateZ = interpolate(slamSpring, [0, 1], [-3, 0]);

            // Active breathing
            const breathe = isActive ? 1 + Math.sin(frame * 0.1) * 0.008 : 1;

            // Number color
            const numBg = isActive
              ? `linear-gradient(135deg, ${theme.primary}, ${theme.accent || theme.primary})`
              : isPast
              ? `linear-gradient(135deg, ${theme.primary}88, ${theme.primary}44)`
              : "rgba(255,255,255,0.06)";

            const badgeSize = listItemH * 0.92;

            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: listItemH,
                  marginBottom: 10,
                  transform: `translateX(${slideX}px) scale(${slamScale * breathe}) rotate(${rotateZ}deg)`,
                  opacity: itemOpacity,
                  transformOrigin: "left center",
                }}
              >
                {/* Rank number */}
                <div style={{
                  position: "relative",
                  width: badgeSize,
                  height: badgeSize,
                  borderRadius: 14,
                  background: numBg,
                  border: isActive ? `2px solid ${theme.primary}` : isPast ? "none" : "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: isActive
                    ? `0 0 ${30 * pulse}px ${theme.primary}aa, 0 0 60px ${theme.primary}44, 0 4px 15px rgba(0,0,0,0.4)`
                    : "0 2px 10px rgba(0,0,0,0.3)",
                }}>
                  {/* Crown for #1 */}
                  {isNumber1 && isActive && (
                    <div style={{
                      position: "absolute",
                      top: -24,
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}>
                      <CrownIcon size={28} color="#ffd700" />
                    </div>
                  )}
                  <span style={{
                    fontFamily: "'Impact', 'Anton', system-ui, sans-serif",
                    fontSize: badgeSize * 0.6,
                    fontWeight: 900,
                    color: isActive || isPast ? "#fff" : `${theme.text}88`,
                    lineHeight: 1,
                    textShadow: isActive ? `0 0 10px rgba(255,255,255,0.5)` : "none",
                  }}>
                    {rank}
                  </span>
                </div>

                {/* Label */}
                <div style={{
                  marginLeft: 14,
                  flex: 1,
                  overflow: "hidden",
                  position: "relative",
                }}>
                  {/* #1 mystery label: show "???" until active, then reveal */}
                  {isNumber1 && !isActive && !isPast ? (
                    <div style={{
                      fontFamily: "'Impact', 'Anton', system-ui, sans-serif",
                      fontWeight: 900,
                      fontSize: listItemH * 0.52,
                      color: `${theme.primary}88`,
                      letterSpacing: 6,
                      textTransform: "uppercase",
                      textShadow: `0 0 15px ${theme.primary}44`,
                    }}>
                      ???
                    </div>
                  ) : (() => {
                    // For #1, animate the title reveal with a spring delay
                    const revealDelay = isNumber1 ? 12 : 0;
                    const revealSpring = isNumber1
                      ? spring({frame: elapsed - revealDelay, fps, config: {damping: 12, stiffness: 160, mass: 0.6}})
                      : 1;
                    const revealScale = isNumber1 ? interpolate(revealSpring, [0, 1], [1.6, 1]) : 1;
                    const revealOpacity = isNumber1 ? interpolate(revealSpring, [0, 1], [0, 1]) : 1;
                    return (
                      <div style={{
                        fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                        fontWeight: isActive ? 900 : 700,
                        fontSize: listItemH * 0.48,
                        color: isActive ? theme.text : isPast ? `${theme.text}99` : `${theme.text}66`,
                        letterSpacing: isActive ? 1 : 0.3,
                        textTransform: "uppercase",
                        textShadow: isActive
                          ? `0 0 30px ${theme.primary}77, 0 2px 10px rgba(0,0,0,0.5)`
                          : "0 1px 4px rgba(0,0,0,0.3)",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        transform: `scale(${revealScale})`,
                        opacity: revealOpacity,
                        transformOrigin: "left center",
                      }}>
                        {it.label}
                      </div>
                    );
                  })()}
                </div>

                {/* Active arrow indicator */}
                {isActive && (
                  <div style={{
                    width: 0,
                    height: 0,
                    borderTop: "8px solid transparent",
                    borderBottom: "8px solid transparent",
                    borderLeft: `12px solid ${theme.primary}`,
                    marginRight: 8,
                    filter: `drop-shadow(0 0 6px ${theme.primary})`,
                    opacity: fastPulse,
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* ── Media card: full-width cinematic reveal ──────────── */}
        <div style={{position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none"}}>
        {items.map((it, i) => {
          const src = getMediaSrc(it.media);
          if (!src) return null;
          const mediaType = resolveMediaType(it);
          const rank = n - i;
          const isNumber1 = rank === 1;
          const elapsed = frame - starts[i];

          const cardSpring = spring({
            frame: elapsed,
            fps,
            config: {damping: 12, stiffness: 120, mass: 0.9},
          });

          // Fade in, hold, fade out
          const o = interpolate(
            frame,
            [starts[i], starts[i] + itemInFrames, ends[i] - itemInFrames, ends[i]],
            [0, 1, 1, 0],
            {extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.ease}
          );

          // Dramatic zoom reveal
          const cardScale = interpolate(cardSpring, [0, 1], [1.15, 1]);
          const cardY = interpolate(cardSpring, [0, 1], [60, 0]);

          // Slow Ken Burns drift while active
          const kenBurns = 1 + (elapsed / 500) * 0.05;

          return (
            <div key={`media-${i}`}>
              {/* Card container */}
              <div style={{
                position: "absolute",
                left: cardLeft,
                top: cardTop,
                width: cardW,
                height: cardH,
                opacity: o,
                transform: `translateY(${cardY}px) scale(${cardScale})`,
              }}>

                {/* Outer glow */}
                <div style={{
                  position: "absolute",
                  inset: -30,
                  borderRadius: 40,
                  background: `radial-gradient(ellipse at center, ${theme.primary}40, transparent 70%)`,
                  filter: "blur(30px)",
                  opacity: o * pulse,
                  pointerEvents: "none",
                }} />

                {/* Card */}
                <div style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: 22,
                  overflow: "hidden",
                  border: `2px solid ${theme.primary}55`,
                  boxShadow: `
                    0 30px 80px rgba(0,0,0,0.7),
                    0 0 0 1px rgba(255,255,255,0.08),
                    0 0 40px ${theme.primary}33,
                    inset 0 1px 0 rgba(255,255,255,0.1)
                  `,
                }}>
                  {/* Media with Ken Burns */}
                  <div style={{
                    width: "100%",
                    height: "100%",
                    transform: `scale(${kenBurns})`,
                    transformOrigin: "center center",
                  }}>
                    <MediaRenderer item={it} />
                  </div>

                  {/* Top shine */}
                  <div style={{
                    position: "absolute",
                    top: 0, left: 0, right: 0, height: 2,
                    background: `linear-gradient(90deg, transparent 10%, ${theme.primary}88 50%, transparent 90%)`,
                    pointerEvents: "none",
                  }} />

                  {/* Bottom gradient for label */}
                  <div style={{
                    position: "absolute",
                    bottom: 0, left: 0, right: 0,
                    height: "45%",
                    background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                    pointerEvents: "none",
                  }} />

                  {/* Rank badge overlay – bottom left */}
                  <div style={{
                    position: "absolute",
                    bottom: 20,
                    left: 20,
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 14,
                  }}>
                    {/* Big rank number */}
                    <div style={{
                      fontFamily: "'Impact', 'Anton', system-ui, sans-serif",
                      fontSize: 90,
                      fontWeight: 900,
                      lineHeight: 0.85,
                      color: theme.text,
                      textShadow: `
                        0 0 30px ${theme.primary}aa,
                        0 0 60px ${theme.primary}55,
                        3px 3px 0 ${theme.primary}
                      `,
                      WebkitTextStroke: `2px ${theme.primary}`,
                    }}>
                      #{rank}
                    </div>
                    {/* Label */}
                    <div style={{
                      fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                      fontSize: 28,
                      fontWeight: 800,
                      color: theme.text,
                      textTransform: "uppercase",
                      letterSpacing: 2,
                      lineHeight: 1.1,
                      textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                      paddingBottom: 8,
                      maxWidth: cardW - 160,
                    }}>
                      {it.label}
                    </div>
                  </div>

                  {/* #1 special: Gold border flash */}
                  {isNumber1 && (
                    <div style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 20,
                      border: `3px solid #ffd70088`,
                      boxShadow: `inset 0 0 40px #ffd70022, 0 0 40px #ffd70033`,
                      opacity: fastPulse,
                      pointerEvents: "none",
                    }} />
                  )}
                </div>
              </div>

              {/* Particle burst on reveal */}
              <ParticleBurst
                frame={frame}
                startAt={starts[i]}
                color={isNumber1 ? "#ffd700" : theme.primary}
                cx={width / 2}
                cy={cardTop + cardH * 0.3}
                count={isNumber1 ? 24 : 14}
              />

              {/* Energy rings */}
              <EnergyRing
                frame={frame}
                startAt={starts[i]}
                color={isNumber1 ? "#ffd700" : theme.primary}
                cx={width / 2}
                cy={cardTop + cardH * 0.3}
              />
              <EnergyRing
                frame={frame}
                startAt={starts[i]}
                color={theme.accent || theme.primary}
                cx={width / 2}
                cy={cardTop + cardH * 0.3}
                delay={6}
              />
            </div>
          );
        })}

        </div>

        {/* ── Number 1 special crown celebration ───────────────── */}
        {activeIndex === n - 1 && items[n - 1] && (() => {
          const elapsed = frame - starts[n - 1];
          const crownSpring = spring({frame: elapsed - 10, fps, config: {damping: 10, stiffness: 140}});
          const crownScale = interpolate(crownSpring, [0, 1], [3, 1]);
          const crownOpacity = interpolate(crownSpring, [0, 1], [0, 1]);
          const crownY = interpolate(crownSpring, [0, 1], [-100, 0]);
          return (
            <div style={{
              position: "absolute",
              top: cardTop - 50,
              left: "50%",
              transform: `translateX(-50%) translateY(${crownY}px) scale(${crownScale})`,
              opacity: crownOpacity,
              filter: `drop-shadow(0 0 20px #ffd700aa)`,
              pointerEvents: "none",
            }}>
              <CrownIcon size={64} color="#ffd700" />
            </div>
          );
        })()}

        {/* ── Vignette frame ───────────────────────────────────── */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          boxShadow: `inset 0 0 ${Math.round(width * 0.15)}px rgba(0,0,0,0.4)`,
        }} />

      </AbsoluteFill>
    </Blur>
  );
};

export default TopCountdown;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
